import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type AccessPolicy, type Report } from '../lib/api';

interface PolicyEditorProps {
  initial?: AccessPolicy;
  onChange: (policy: AccessPolicy) => void;
  showRoc?: boolean;
}

const DOC_TYPES = ['PCI', 'SOC', 'ISO', 'Other'] as const;

type ApprovalMode = 'category' | 'individual';
type ExpiryMode = 'perpetual' | 'days' | 'date';

export function PolicyEditor({ initial, onChange, showRoc }: PolicyEditorProps) {
  const { t } = useTranslation();
  const [allReports, setAllReports] = useState<Report[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  const hasInitialTypes = (initial?.approvedTypes?.length ?? 0) > 0;
  const hasInitialFiles = (initial?.approvedReportIds?.length ?? 0) > 0;
  const [mode, setMode] = useState<ApprovalMode>(
    hasInitialFiles && !hasInitialTypes ? 'individual' : 'category',
  );

  const [approvedTypes, setApprovedTypes] = useState<Set<string>>(
    () => new Set(initial?.approvedTypes ?? ['PCI']),
  );
  const [approvedTypesYear, setApprovedTypesYear] = useState<number | null>(
    () => initial?.approvedTypesYear ?? null,
  );
  const [rocReportIds, setRocReportIds] = useState<Set<string>>(
    () => new Set(initial?.rocReportIds ?? []),
  );
  const [approvedReportIds, setApprovedReportIds] = useState<Set<string>>(
    () => new Set(initial?.approvedReportIds ?? []),
  );

  const [accessMode, setAccessMode] = useState<'view' | 'download'>(
    () => initial?.accessMode ?? 'download',
  );

  // Expiry state
  const initialExpiryMode: ExpiryMode = initial?.expiresAt ? 'date' : 'perpetual';
  const [expiryMode, setExpiryMode] = useState<ExpiryMode>(initialExpiryMode);
  const [expiryDays, setExpiryDays] = useState<number>(90);
  const [expiryDate, setExpiryDate] = useState<string>(
    () => initial?.expiresAt ?? '',
  );

  useEffect(() => {
    setLoadingReports(true);
    api.listReports().then((data) => {
      setAllReports(data.reports);
    }).catch(() => {}).finally(() => setLoadingReports(false));
  }, []);

  useEffect(() => {
    let computedExpiry: string | null = null;
    if (expiryMode === 'days') {
      const d = new Date();
      d.setDate(d.getDate() + expiryDays);
      computedExpiry = d.toISOString().slice(0, 10);
    } else if (expiryMode === 'date' && expiryDate) {
      computedExpiry = expiryDate;
    }

    if (mode === 'category') {
      onChange({
        approvedTypes: Array.from(approvedTypes),
        approvedTypesYear: approvedTypesYear,
        rocReportIds: Array.from(rocReportIds),
        approvedReportIds: [],
        accessMode,
        expiresAt: computedExpiry,
      });
    } else {
      onChange({
        approvedTypes: [],
        approvedTypesYear: null,
        rocReportIds: Array.from(rocReportIds),
        approvedReportIds: Array.from(approvedReportIds),
        accessMode,
        expiresAt: computedExpiry,
      });
    }
  }, [mode, approvedTypes, approvedTypesYear, rocReportIds, approvedReportIds, accessMode, expiryMode, expiryDays, expiryDate]);

  function toggleType(type: string) {
    setApprovedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  function toggleRocReport(id: string) {
    setRocReportIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleApprovedReport(id: string) {
    setApprovedReportIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const rocReports = allReports.filter((r) => r.docType === 'ROC');
  const nonRocReports = allReports.filter((r) => r.docType !== 'ROC');
  const currentYear = new Date().getFullYear();

  return (
    <div className="policy-editor">
      {/* Access mode: View Only vs View & Download */}
      <fieldset className="policy-section">
        <legend>{t('policyEditor.accessLevel', 'Access Level')}</legend>
        <div className="policy-checklist">
          <label className="aoc-check-row">
            <input
              type="radio"
              name="access-mode"
              checked={accessMode === 'download'}
              onChange={() => setAccessMode('download')}
            />
            <span className="aoc-check-label">
              <strong>{t('policyEditor.viewAndDownload', 'View & Download')}</strong>
              {' — '}
              {t('policyEditor.viewAndDownloadDesc', 'user can view and download documents')}
            </span>
          </label>
          <label className="aoc-check-row">
            <input
              type="radio"
              name="access-mode"
              checked={accessMode === 'view'}
              onChange={() => setAccessMode('view')}
            />
            <span className="aoc-check-label">
              <strong>{t('policyEditor.viewOnly', 'View Only')}</strong>
              {' — '}
              {t('policyEditor.viewOnlyDesc', 'user can view documents in-browser but cannot download, copy, or print')}
            </span>
          </label>
        </div>
      </fieldset>

      {/* Mode selector */}
      <fieldset className="policy-section">
        <legend>{t('policyEditor.accessMode', 'Access Grant Method')}</legend>
        <div className="policy-checklist">
          <label className="aoc-check-row">
            <input
              type="radio"
              name="approval-mode"
              checked={mode === 'category'}
              onChange={() => setMode('category')}
            />
            <span className="aoc-check-label">
              <strong>{t('policyEditor.byCategory', 'By document category')}</strong>
              {' — '}
              {t('policyEditor.byCategoryDesc', 'grant access to all documents of a type')}
            </span>
          </label>
          <label className="aoc-check-row">
            <input
              type="radio"
              name="approval-mode"
              checked={mode === 'individual'}
              onChange={() => setMode('individual')}
            />
            <span className="aoc-check-label">
              <strong>{t('policyEditor.byFile', 'By specific file')}</strong>
              {' — '}
              {t('policyEditor.byFileDesc', 'grant access to individual documents only')}
            </span>
          </label>
        </div>
      </fieldset>

      {/* Category-based approval */}
      {mode === 'category' && (
        <fieldset className="policy-section" style={{ marginTop: '1rem' }}>
          <legend>{t('policyEditor.approvedTypes', 'Approved Document Types')}</legend>
          <div className="policy-checklist">
            {DOC_TYPES.map((type) => (
              <label key={type} className="aoc-check-row">
                <input
                  type="checkbox"
                  checked={approvedTypes.has(type)}
                  onChange={() => toggleType(type)}
                />
                <span className="aoc-check-label">
                  <span className={`badge badge-${type.toLowerCase()}`} style={{ marginRight: '6px' }}>{type}</span>
                  {type === 'PCI' && t('policyEditor.pciDesc', 'PCI DSS Attestations of Compliance')}
                  {type === 'SOC' && t('policyEditor.socDesc', 'SOC Reports')}
                  {type === 'ISO' && t('policyEditor.isoDesc', 'ISO 27001 Certificates')}
                  {type === 'Other' && t('policyEditor.otherDesc', 'Other Compliance Documents')}
                </span>
              </label>
            ))}
          </div>

          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <label htmlFor="policy-year-scope" style={{ fontWeight: 500 }}>
              {t('policyEditor.yearScope', 'Year Scope')}
            </label>
            <select
              id="policy-year-scope"
              value={approvedTypesYear ?? 'perpetual'}
              onChange={(e) => setApprovedTypesYear(
                e.target.value === 'perpetual' ? null : parseInt(e.target.value, 10),
              )}
              style={{ marginTop: '0.25rem' }}
            >
              <option value="perpetual">
                {t('policyEditor.allYears', 'All years — past and future documents')}
              </option>
              <option value={currentYear}>
                {t('policyEditor.currentYearOnly', '{year} only — current year documents', { year: currentYear })}
              </option>
            </select>
          </div>
        </fieldset>
      )}

      {/* Individual file approval */}
      {mode === 'individual' && (
        <fieldset className="policy-section" style={{ marginTop: '1rem' }}>
          <legend>{t('policyEditor.individualFiles', 'Select Specific Documents')}</legend>
          <p className="form-hint">
            {t('policyEditor.individualHint', 'The user can access only the selected documents.')}
          </p>
          {loadingReports ? (
            <p className="form-hint">{t('policyEditor.loading', 'Loading...')}</p>
          ) : nonRocReports.length === 0 ? (
            <p className="form-hint">{t('policyEditor.noFiles', 'No documents uploaded yet.')}</p>
          ) : (
            <div className="policy-checklist">
              {nonRocReports.map((r) => (
                <label key={r.id} className="aoc-check-row">
                  <input
                    type="checkbox"
                    checked={approvedReportIds.has(r.id)}
                    onChange={() => toggleApprovedReport(r.id)}
                  />
                  <span className="aoc-check-label">
                    <span className={`badge badge-${(r.docType || 'pci').toLowerCase()}`} style={{ marginRight: '6px' }}>{r.docType || 'PCI'}</span>
                    {r.friendlyName || r.id}
                    {r.year && (
                      <span className="aoc-check-date"> · {r.year}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
      )}

      {/* Access Duration / Expiry */}
      <fieldset className="policy-section" style={{ marginTop: '1rem' }}>
        <legend>{t('policyEditor.duration', 'Access Duration')}</legend>
        <div className="policy-checklist">
          <label className="aoc-check-row">
            <input
              type="radio"
              name="expiry-mode"
              checked={expiryMode === 'perpetual'}
              onChange={() => setExpiryMode('perpetual')}
            />
            <span className="aoc-check-label">
              <strong>{t('policyEditor.noExpiry', 'No expiry')}</strong>
              {' — '}
              {t('policyEditor.noExpiryDesc', 'access does not expire')}
            </span>
          </label>
          <label className="aoc-check-row">
            <input
              type="radio"
              name="expiry-mode"
              checked={expiryMode === 'days'}
              onChange={() => setExpiryMode('days')}
            />
            <span className="aoc-check-label">
              <strong>{t('policyEditor.expiresInDays', 'Expires in')}</strong>
              {' '}
              <input
                type="number"
                min={1}
                max={3650}
                value={expiryDays}
                onChange={(e) => {
                  setExpiryDays(Math.max(1, parseInt(e.target.value, 10) || 1));
                  setExpiryMode('days');
                }}
                style={{ width: '70px', margin: '0 6px' }}
                onClick={(e) => e.stopPropagation()}
              />
              {t('policyEditor.days', 'days')}
            </span>
          </label>
          <label className="aoc-check-row">
            <input
              type="radio"
              name="expiry-mode"
              checked={expiryMode === 'date'}
              onChange={() => setExpiryMode('date')}
            />
            <span className="aoc-check-label">
              <strong>{t('policyEditor.expiresOnDate', 'Expires on')}</strong>
              {' '}
              <input
                type="date"
                value={expiryDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => {
                  setExpiryDate(e.target.value);
                  setExpiryMode('date');
                }}
                style={{ margin: '0 6px' }}
                onClick={(e) => e.stopPropagation()}
              />
            </span>
          </label>
        </div>
      </fieldset>

      {/* ROC section (always per-document, regardless of mode) */}
      {showRoc && (
        <fieldset className="policy-section" style={{ marginTop: '1rem' }}>
          <legend>{t('policyEditor.rocGrants', 'ROC Access (per-document)')}</legend>
          <p className="form-hint">
            {t('policyEditor.rocHint', 'ROC documents require explicit per-document approval. Select specific ROC documents to grant access.')}
          </p>
          {loadingReports ? (
            <p className="form-hint">{t('policyEditor.loading', 'Loading...')}</p>
          ) : rocReports.length === 0 ? (
            <p className="form-hint">{t('policyEditor.noRocs', 'No ROC documents uploaded yet.')}</p>
          ) : (
            <div className="policy-checklist">
              {rocReports.map((r) => (
                <label key={r.id} className="aoc-check-row">
                  <input
                    type="checkbox"
                    checked={rocReportIds.has(r.id)}
                    onChange={() => toggleRocReport(r.id)}
                  />
                  <span className="aoc-check-label">
                    {r.friendlyName || r.id}
                    {r.year && (
                      <span className="aoc-check-date"> · {r.year}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
}
